import { isPhoneChangeAllowed, selectBasket, selectBasketCustomer, sendBasket, updateCustomerData } from 'containers/OrderDetails/basketSlice'
import { fetchPreregister, selectIsLoggedIn, selectRecovery, selectUser } from 'containers/Login/loginSlice'
import { getPhoneParts, isValidPhone, PhoneValue } from 'utils/phone'
import { useReduxDispatch, useReduxSelector } from 'store'
import { useHistory, useLocation } from 'react-router-dom'
import useSwitchAccount from 'hooks/useSwitchAccount'
import PhoneInput from 'components/inputs/PhoneInput'
import ArrowBtn from 'components/buttons/ArrowBtn'
import { LOCATION_STATES } from 'utils/constants'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import React, { useEffect, useRef, useState } from 'react'
import ResendCode from 'components/ResendCode'
import { toPhoneString } from 'utils/string'
import Popup from 'components/popups/Popup'
import PinForm from 'components/PinForm'
import { POST } from 'rest/request'
import * as urls from 'rest/urls'

interface SignAgreementPopupProps {
  contractGroupId: string
  outsideCart?: boolean
  callback: (signature: Signature, replacedBy?: string) => void
}

export interface Signature {
  name: string
  type: string
  raw_type?: string
  date: string
  phone?: string
  email?: string
  device?: string
  ip?: string
  image_url?: string
}

interface Response {
  result: boolean
  error?: string
  recipient?: string
  additional_recipient?: string
  signature?: Signature | null
  /** id NOWEJ grupy — backend wymienił grupę z nieważnym podpisem (§2a) */
  replacedBy?: string
  /** grupa koszyka należy do innego klienta niż zalogowany — trzeba zsynchronizować koszyk (§2b) */
  customerChanged?: boolean
}

const type = process.env.REACT_APP_TYPE

const SignAgreementPopup: React.FC<SignAgreementPopupProps> = ({ contractGroupId, outsideCart, callback }) => {
  const [isLoading, setLoading] = useState(false)
  const [recipient, setRecipient] = useState('')
  const [channels, setChannels] = useState<{ primary?: string, additional?: string }>({})
  const [warning, setWarning] = useState('')
  const [code, setCode] = useState('')
  const [phoneStep, setPhoneStep] = useState<null | 'edit' | 'taken'>(null)
  const [newPhone, setNewPhone] = useState<PhoneValue>(getPhoneParts())
  const [groupId, setGroupId] = useState(contractGroupId)

  const sentDate = useRef<number>(0)

  const { cartId } = useReduxSelector(selectBasket)
  const customer = useReduxSelector(selectBasketCustomer)
  const isLoggedIn = useReduxSelector(selectIsLoggedIn)
  const recovery = useReduxSelector(selectRecovery)

  const { switchAccount, isPending: isSwitchingAccount } = useSwitchAccount()
  const { goBack, replace } = useHistory()
  const { pathname, search } = useLocation()
  const dispatch = useReduxDispatch()
  const { t } = useTranslation('contract')
  const { t: tForm } = useTranslation('form')

  const user = useReduxSelector(selectUser)
  useEffect(() => {
    setGroupId(contractGroupId)
  }, [contractGroupId])

  const resetCode = () => {
    setRecipient('')
    setChannels({})
    setCode('')
  }

  /** Sync koszyka krokiem `contract` — backend przepisze grupę na bieżącego klienta albo założy nową (§2b) */
  const syncCartContract = async (): Promise<{ id?: string } | undefined> => {
    if (outsideCart) return
    const res: any = await dispatch(sendBasket({ step: 'contract' }))
    return res?.payload?.contractgroups?.[0]
  }

  const canChangePhone = !outsideCart && isPhoneChangeAllowed(customer, user)
  const currentPhone = !outsideCart && customer?.phone ? toPhoneString(customer.phone, customer.prefix) : ''

  const startEditingPhone = () => {
    if (isLoading) return
    setWarning('')
    setNewPhone(getPhoneParts(customer?.phone, customer?.prefix))
    setPhoneStep('edit')
  }

  const isSamePhone = (value: PhoneValue) => {
    const current = getPhoneParts(customer?.phone, customer?.prefix)
    return current.prefix === value.prefix && current.phone === value.phone
  }

  const isPhoneAvailable = async (value: PhoneValue) => {
    try {
      const response = await dispatch(fetchPreregister(value)).unwrap()
      return response?.valid !== false
    } catch (error) {
      console.warn('Fetch preregister failed')
      return true
    }
  }

  const savePhone = async () => {
    if (isLoading) return
    if (!isValidPhone(newPhone.prefix, newPhone.phone)) {
      setWarning(t('wrongPhoneNumber'))
      return
    }

    if (isSamePhone(newPhone)) {
      setWarning('')
      setPhoneStep(null)
      return
    }

    setWarning('')
    setLoading(true)
    try {
      if (!(await isPhoneAvailable(newPhone))) {
        setPhoneStep('taken')
        return
      }

      const response = await POST(urls.cartCustomer, {
        body: { cartId, customer: { prefix: newPhone.prefix, phone: newPhone.phone } }
      })
      dispatch(updateCustomerData(response))
      resetCode()
      setPhoneStep(null)
      // Zmiana danych klienta: niepodpisana grupa dostaje nowy PDF, a wcześniej wysłany kod jest unieważniony (§2b)
      const group = await syncCartContract()
      if (group?.id) setGroupId(group.id)
    } catch (error) {
      setWarning(t('phoneChangeFailed'))
    } finally {
      setLoading(false)
    }
  }

  const request = async (endpoint: 'sign' | 'sendsmscode', body: Record<string, unknown> | undefined, id: string, canRetry: boolean): Promise<boolean> => {
    const url = `${urls.contractgroups}/${id}/${endpoint}`
    const response: Response = await POST(url, { body })

    if (response.customerChanged) {
      // Nic nie zostało wysłane ani podpisane — sync koszyka i ponowienie wysyłki kodu na grupie z odpowiedzi
      resetCode()
      const group = canRetry ? await syncCartContract() : undefined
      if (group?.id) {
        setWarning('')
        setGroupId(group.id)
        return request('sendsmscode', undefined, group.id, false)
      }
      setWarning(response.error || '')
      return false
    }

    if (response.replacedBy) setGroupId(response.replacedBy)
    if (response.signature) {
      callback(response.signature, response.replacedBy)
      goBack()
    }
    if (response.recipient) {
      sentDate.current = Date.now()
      setRecipient(response.recipient)
      setChannels(c => ({
        primary: body?.additional ? c.primary : response.recipient,
        additional: response.additional_recipient || c.additional
      }))
    }
    if (response.error) setWarning(response.error)
    return !!response.recipient
  }

  const send = async (endpoint: 'sign' | 'sendsmscode', body?: Record<string, unknown>) => {
    if (isLoading) return
    setLoading(true)
    try {
      const result = await request(endpoint, body, groupId, !outsideCart)
      setLoading(false)
      return result

    } catch (error) {
      setLoading(false)
      return false
    }
  }

  const sendCode = (additional = false) => send('sendsmscode', additional ? { additional: '1' } : undefined)

  const validateForm = () => {
    if (recipient) {
      if (code) {
        setWarning('')
        send(
          'sign',
          outsideCart
            ? { code, outsideCart: true }
            : { code }
        )
      } else {
        setWarning('Wpisz kod!')
      }

    } else {
      sendCode()
    }
  }

  if (phoneStep === 'taken') {
    const canRecover = !!recovery?.token && !!recovery.login?.phone
    return (
      <Popup>
        <div className='signAgreement__popup'>
          <div className='popup__title'>
            <div>{t('changePhoneNumberTitle')}</div>
          </div>
          <p>
            {tForm('phoneNumberHasAlreadyBeenUsed')}{' '}
            {tForm('wouldYouLikeToLogInInstead')}
          </p>
          {isLoggedIn && <p>{t('switchAccountInfo')}</p>}
          <div className='signAgreement__buttons'>
            <ArrowBtn
              enlarged
              isLoading={isSwitchingAccount}
              text={tForm('logIn')}
              action={() => {
                if (isLoggedIn) switchAccount(`/login${newPhone.phone ? `?login=${newPhone.phone}` : ''}`)
                else replace(pathname + search, LOCATION_STATES.popup({ type: 'login' }))
              }}
            />
            {canRecover && <ArrowBtn
              enlarged
              showArrow={false}
              className='--black'
              text={tForm('recoverAccess')}
              action={() => {
                if (isLoggedIn) switchAccount('/login/recovery', { recoveryLogin: recovery.login })
                else replace('/login/recovery')
              }}
            />}
            <BackBtn
              text={t('useAnotherNumber')}
              action={() => !isSwitchingAccount && setPhoneStep('edit')}
            />
          </div>
        </div>
      </Popup>
    )
  }

  if (phoneStep === 'edit') return (
    <Popup>
      <div className='signAgreement__popup'>
        <div className='popup__title'>
          <div>{t('changePhoneNumberTitle')}</div>
        </div>
        <p>{t('changePhoneNumberDescription')}</p>
        <PhoneInput
          required
          autoFocus
          name='phone'
          value={newPhone.phone}
          prefix={newPhone.prefix}
          disabled={isLoading}
          warning={!!warning}
          label={t('phoneNumber')}
          enterClicked={savePhone}
          setValue={value => {
            setNewPhone(value)
            if (warning) setWarning('')
          }}
        />
        {warning && <p className='--error'>{warning}</p>}
        <div className='signAgreement__buttons'>
          <ArrowBtn
            enlarged
            isLoading={isLoading}
            text={t('save')}
            action={savePhone}
          />
          <BackBtn
            text={t('cancel')}
            action={() => {
              if (isLoading) return
              setWarning('')
              setPhoneStep(null)
            }}
          />
        </div>
      </div>
    </Popup>
  )

  return (
    <Popup>
      <div className='signAgreement__popup'>
        <div className='popup__title'>
          <div>{t('signAgreementTitle')}</div>
        </div>
        <p>{t(type === 'rze01' ? 'signAgreementDescription2' : 'signAgreementDescription')}</p>
        {!recipient && currentPhone && <p className='signAgreement__phone group'>
          <span>{t('codeWillBeSentTo')}: <b>{currentPhone}</b></span>
          {canChangePhone && <span className='--link' onClick={startEditingPhone}>{t('changePhoneNumber')}</span>}
        </p>}
        {recipient && <p className='signAgreement__phone group'>
          <span>{t('sentTo')}: <b>{toPhoneString(recipient)}</b></span>
          {canChangePhone && <span
            className='--link'
            onClick={startEditingPhone}>
            {t('changePhoneNumber')}
          </span>}
          <ResendCode
            inline
            text={t('resend')}
            recipient={toPhoneString(channels.primary || recipient)}
            additionalRecipient={channels.additional && toPhoneString(channels.additional)}
            send={sendCode}
            cooldown={60_000}
            sentAt={sentDate.current}
          />
        </p>}
        {warning && <p className='--error'>{warning}</p>}
        {recipient && <PinForm
          isCode
          value={code}
          warning={!!warning}
          setValue={setCode}
          autoLoadCodeFromSMS
        />}
        {recipient && <p className='signAgreement__declaration'>*{t('signAgreementDeclaration')}</p>}
        <div className='signAgreement__buttons'>
          <ArrowBtn
            enlarged
            isLoading={isLoading}
            text={t(recipient ? 'confirm' : 'sendCode')}
            action={validateForm}
          />
          <BackBtn
            text={t('cancel')}
            action={goBack}
          />
        </div>
      </div>
    </Popup>
  )
}

export default SignAgreementPopup